import { Icons } from '@cockpitai/components/atoms'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { routesPath } from '@cockpitai/constants/routesPath'
import { useLevelNavigation } from '@cockpitai/store/navigation/useLevelNavigation'
import {
  useSideBarStore,
  type Favorite,
} from '@cockpitai/store/navigation/useSideBarStore'
import { goToScrollTop } from '@cockpitai/utils'
import { AnimatePresence, motion } from 'framer-motion'
import Lottie from 'lottie-react'
import { useEffect, useMemo, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import { useSendFavorite } from '@cockpitai/integration/favorites/hooks'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import { hideWord } from '@cockpitai/utils/hideWord'
import { urlDecipher } from '@cockpitai/utils/urlCipher'
import { useNavigationType } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import animationData from './startAnimation.json'

function breadcrumbNavTo(encodedData: string): string {
  if (!encodedData) {
    return routesPath.dashboard.home
  }

  const raw = urlDecipher(encodedData)

  if (raw.startsWith('/')) {
    return raw
  }

  return `${routesPath.dashboard.home}${raw}`
}

function isGroupsBreadcrumbItem(item: { name: string; data: string }): boolean {
  if (item.name === 'Totalizador' || item.name.includes('Grupos')) {
    return true
  }

  if (!item.data) {
    return false
  }

  const raw = urlDecipher(item.data)

  return raw.includes('/dashboard/grupos/')
}

function affiliateContextFromCipher(encodedData: string): {
  affiliateId: string | null
  occupationType: string | null
  withoutWallet: string | null
} {
  if (!encodedData) {
    return { affiliateId: null, occupationType: null, withoutWallet: null }
  }

  const raw = urlDecipher(encodedData)

  try {
    if (raw.startsWith('/')) {
      const u = new URL(raw, window.location.origin)

      return {
        affiliateId: u.searchParams.get('affiliateId'),
        occupationType: u.searchParams.get('occupationType'),
        withoutWallet: u.searchParams.get('withoutWallet'),
      }
    }

    const qs = raw.startsWith('?') ? raw : `?${raw}`
    const params = new URLSearchParams(qs)

    return {
      affiliateId: params.get('affiliateId'),
      occupationType: params.get('occupationType'),
      withoutWallet: params.get('withoutWallet'),
    }
  } catch {
    return { affiliateId: null, occupationType: null, withoutWallet: null }
  }
}

export function NavigationLevel() {
  const [stared, setStared] = useState<string>('')
  const navigationType = useNavigationType()

  const { mutateAsync } = useSendFavorite()

  const navigationHistory = useLevelNavigation((s) => s.navigationHistory)
  const resetNavigation = useLevelNavigation((s) => s.resetNavigation)
  const removeNavigation = useLevelNavigation((s) => s.removeNavigation)
  const setInTransition = useLevelNavigation((s) => s.setInTransition)

  const { addFavorite, favorites, updateFavoriteItem } = useSideBarStore()

  const handleTransition = () => {
    setInTransition(true)

    setTimeout(() => {
      setInTransition(false)
    }, 1000)
  }

  const navigate = useNavigate()
  const { pathname, search } = useLocation()

  const { affiliateId, occupationType, withoutWallet } = useMemo(() => {
    const params = new URLSearchParams(search)

    return {
      affiliateId: params.get('affiliateId'),
      occupationType: params.get('occupationType'),
      withoutWallet: params.get('withoutWallet'),
    }
  }, [search])

  const isGroupDetailRoute = pathname.includes('/dashboard/grupos/')

  useEffect(() => {
    if (!affiliateId && !isGroupDetailRoute) {
      resetNavigation()
    }
  }, [affiliateId, isGroupDetailRoute, resetNavigation])

  useEffect(() => {
    // Adiciona verificação para evitar redirecionamento desnecessário no carregamento inicial
    if (navigationType === 'POP' && navigationHistory.length > 0) {
      const itemIndex = navigationHistory.findIndex((item) => {
        if (!item.data) {
          return false
        }

        const ctx = affiliateContextFromCipher(item.data)

        return (
          ctx.affiliateId === affiliateId &&
          ctx.occupationType === occupationType &&
          ctx.withoutWallet === withoutWallet
        )
      })

      if (itemIndex !== -1) {
        removeNavigation(itemIndex)
      } else {
        resetNavigation()
        navigate(routesPath.dashboard.home, { replace: true })
      }
    }
  }, [navigationType, affiliateId, withoutWallet, occupationType, navigationHistory.length, navigate, removeNavigation, resetNavigation])

  const handleHistoryNavigationAtIndex = (index: number) => {
    goToScrollTop()

    handleTransition()

    if (index === 0) {
      resetNavigation()

      return
    }

    removeNavigation(index)
  }

  const handleAddToFav = (item: Favorite) => async () => {
    addFavorite(item)

    item?.data && setStared(item.data)

    try {
      const favorite = await mutateAsync({
        name: item.name,
        data: item.data,
      })

      updateFavoriteItem(favorite)

      sendAnalyticsEvent('hierarquia_favorite_added', {
        item_name: item.name
      })

    } catch (error) {
      console.error(error)
    }

    setTimeout(() => {
      setStared('')
    }, 1000)
  }

  useEffect(() => {
    if (navigationHistory.length > 0) {
      const breadcrumbTree = navigationHistory.map(item => item.name).join(' / ')
      sendAnalyticsEvent('hierarquia', { breadcrumb: breadcrumbTree })
    }

  }, [navigationHistory])

  return navigationHistory.length > 0 ? (
    <div className="level-navigation-menu">
      {navigationHistory.map((item, index) => {
        const alreadyFavorite = favorites.some(
          (fav) => fav.data === item.data && fav.name === item.name,
        )

        return (
          <div
            key={`level-navigation-item-${index}`}
            className="level-navigation-item">
            {index > 0 && !isGroupsBreadcrumbItem(item) && (
              <button
                className={twMerge(
                  'relative',
                  alreadyFavorite && 'pointer-events-none',
                )}
                onClick={handleAddToFav({
                  data: item.data,
                  name: item.name,
                  id: item.id,
                })}>
                <Icons.Star
                  width={14}
                  height={14}
                  className={alreadyFavorite ? 'text-others-orange' : ''}
                />

                <AnimatePresence>
                  {stared === item.data && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}>
                      <Lottie
                        style={{
                          position: 'absolute',
                          width: 24,
                          height: 24,
                          top: '-5px',
                          left: '-5px',
                        }}
                        animationData={animationData}
                        loop
                        autoPlay
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            )}
            {index < navigationHistory.length - 1 ? (
              <NavLink
                to={breadcrumbNavTo(item.data)}
                onClick={() => handleHistoryNavigationAtIndex(index)}>
                <Typography className="whitespace-nowrap text-sm" font="outfit">
                  {hideWord('REDE ', item.name)}
                </Typography>
              </NavLink>
            ) : (
              <Typography className="whitespace-nowrap text-sm" font="outfit">
                {hideWord('REDE ', item.name)}
              </Typography>
            )}
            {index < navigationHistory.length - 1 && (
              <span className="pr-2 text-xs text-gray-400 lg:px-5">/</span>
            )}
          </div>
        )
      })}
    </div>
  ) : (
    <></>
  )
}
